import { supabase } from "../lib/supabase";
import { PAGE_SIZE } from "./billableConversionsExportService";

// Every advertiser, whatever its billing method, for the QuickBooks invoice
// export's advertiser filter, with its billing method and whether it is
// enrolled in auto-charge (a public.advertiser_auto_charge_settings row with
// enabled = true; a paused account is still enrolled). Support reads both
// tables under RLS.

function client() {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  return supabase;
}

// Every row of a query, by pages. Only an empty page ends it: PostgREST may cap
// a page below PAGE_SIZE, so a short page is not proof that nothing follows.
async function allRows(buildQuery) {
  const out = [];
  for (;;) {
    // Resume after the rows received, not after PAGE_SIZE: a capped page is shorter.
    const { data, error } = await buildQuery().range(out.length, out.length + PAGE_SIZE - 1);
    if (error) throw new Error("Could not load the advertisers.");
    if (!data?.length) return out;
    out.push(...data);
  }
}

/**
 * [{ id, name, billingMethod, autoCharge }] sorted by name; name is the billing
 * company name, else the advertiser name; billingMethod is advertisers.billing_method
 * ("credit_card", "affiliate_platform", "invoice" or null).
 */
export async function fetchExportAdvertisers() {
  const [advertisers, enrolled] = await Promise.all([
    allRows(() => client().from("advertisers").select("id,name,billing_company_name,billing_method").order("id")),
    allRows(() => client().from("advertiser_auto_charge_settings").select("advertiser_id")
      .eq("enabled", true).order("advertiser_id")),
  ]);
  const autoCharge = new Set(enrolled.map((r) => r.advertiser_id));
  return advertisers
    .map((a) => ({
      id: a.id,
      name: a.billing_company_name?.trim() || a.name || `Advertiser ${a.id}`,
      billingMethod: a.billing_method ?? null,
      autoCharge: autoCharge.has(a.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }) || a.id - b.id);
}
